import React, { useState } from 'react';

// Design Tokens
const gold = "#f5a623";
const blue = "#3b82f6", blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const purple = "#a78bfa";
const navy3 = "#1a2236", navy4 = "#212d45";
const white = "#ffffff", textCol = "#cbd5e1", muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

// Shared atoms
const s = {
  section: { marginBottom: 48 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "1.75rem", fontWeight: 800, color: white, lineHeight: 1.3, marginBottom: 16 },
  prose: { fontSize: "0.95rem", color: textCol, lineHeight: 1.8, marginBottom: 20 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "40px 0" },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "24px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function FeatCard({ icon, title, body }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ background: navy3, border: `1px solid ${hov ? "rgba(59,130,246,.35)" : border}`, borderRadius: 8, padding: "22px 20px", transition: "all 0.2s", transform: hov ? "translateY(-2px)" : "none" }}>
      <div style={{ fontSize: "1.4rem", marginBottom: 10 }}>{icon}</div>
      <div style={{ fontSize: "0.85rem", fontWeight: 700, color: white, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65 }}>{body}</div>
    </div>
  );
}

function ColCard({ label, labelColor = gold, accent = gold, title, body, items }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${accent},transparent)` }} />
      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: labelColor, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", fontWeight: 800, color: white, marginBottom: body ? 10 : 0 }}>{title}</div>
      {body && <div style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7 }}>{body}</div>}
      {items && <ul style={{ listStyle: "none", margin: "10px 0 0", padding: 0 }}>{items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 8, fontSize: "0.83rem", color: textCol, padding: "6px 0", borderBottom: i < items.length - 1 ? `1px solid ${border}` : "none" }}>
          <span style={{ color: gold, flexShrink: 0, fontSize: "0.75rem", marginTop: 2 }}>→</span>{it}
        </li>
      ))}</ul>}
    </div>
  );
}

function Pill({ text, color = gold }) {
  return (
    <span style={{ display: "inline-block", fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, color, background: `${color}18`, border: `1px solid ${color}33`, marginRight: 4, marginBottom: 4 }}>
      {text}
    </span>
  );
}

// Section 1: Capital characteristics
function CapitalChars() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6">
      <FeatCard icon="🌍" title="Mobile" body="Capital moves freely to countries and sectors where the investment climate is hospitable and profit opportunities exist." />
      <FeatCard icon="⚡" title="Sensitive" body="Capital reacts quickly to environmental changes: taxation shifts, political instability, inflation, and government regulation." />
      <FeatCard icon="💎" title="Scarce" body="Capital is in short supply and high demand worldwide. Suppliers and users must compete for it — which is why returns matter." />
    </div>
  );
}

// Section 2: Instruments Tabs
const instruments = [
  {
    label: "🏦 Debt", title: "Debt Instruments (Fixed-Income)",
    body: "The issuer promises to repay the principal at maturity and typically makes regular interest payments. Debt investors are creditors, not owners.",
    items: ["Bonds & Debentures — long-term debt issued by corporations and governments", "Mortgages — debt secured by real property", "Treasury Bills (T-bills) — short-term federal government debt, sold at a discount", "Commercial Paper — short-term corporate debt for working capital needs"],
    pills: [{ text: "Fixed Interest", col: gold }, { text: "Maturity Date", col: gold }, { text: "Creditor Rights", col: gold }, { text: "Priority in Bankruptcy", col: gold }],
  },
  {
    label: "📈 Equity", title: "Equity Instruments",
    body: "Represents an ownership stake in a company. Equity holders share in the company's success — and its losses. No guaranteed repayment.",
    items: ["Common Shares — ownership with voting rights; eligible for dividends (not guaranteed) and capital gains", "Preferred Shares — typically non-voting, but have a prior claim on dividends over common shareholders; often fixed dividend rate"],
    pills: [{ text: "Ownership", col: gold }, { text: "Voting Rights", col: gold }, { text: "Dividends", col: blue2 }, { text: "Capital Gains", col: blue2 }],
  },
  {
    label: "🧺 Funds", title: "Investment Funds (Mutual Funds)",
    body: "A company or trust that pools investor money to manage a diversified portfolio of securities. Investors own units or shares and share proportionally in the fund's income and gains.",
    items: ["Professional management with built-in diversification", "Accessible to small investors who couldn't otherwise build a diversified portfolio", "Income, gains, and expenses flow through proportionally to unitholders", "Regulated by provincial commissions; distributed by CIRO-regulated dealers"],
    pills: [{ text: "Pooled Capital", col: gold }, { text: "Diversification", col: gold }, { text: "Unitholders", col: blue2 }],
  },
  {
    label: "⚙️ Derivatives", title: "Derivatives",
    body: "Products derived from an underlying instrument — a stock, index, commodity, or currency. Value fluctuates based on the underlying asset's price movements.",
    items: ["Options — the right (not obligation) to buy or sell an asset at a specified price before a set date", "Forwards/Futures — contractual obligation to buy or sell an asset at a predetermined price on a future date", "Best suited for sophisticated investors due to complexity and leverage risk"],
    pills: [{ text: "Underlying Asset", col: gold }, { text: "Leverage", col: red }, { text: "Sophisticated Investors", col: blue2 }],
  },
  {
    label: "🔧 Other", title: "Other / Engineered Instruments",
    body: "Financial products engineered by combining features of multiple instrument types to meet specific investor needs.",
    items: ["Linked Notes — return is linked to the performance of an index or basket of assets, often with principal protection", "Exchange-Traded Funds (ETFs) — funds that trade on a stock exchange like shares; typically track an index and offer low costs"],
    pills: [{ text: "ETFs", col: gold }, { text: "Linked Notes", col: gold }, { text: "Index Tracking", col: blue2 }],
  },
];

function InstrumentsTabs() {
  const [activeTab, setActiveTab] = useState(0);
  const current = instruments[activeTab];
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {instruments.map((inst, idx) => (
          <button key={idx} onClick={() => setActiveTab(idx)}
            style={{ fontFamily: "system-ui", fontSize: "0.8rem", fontWeight: 600, padding: "8px 14px", borderRadius: 6, border: `1px solid ${activeTab === idx ? gold : border}`, background: activeTab === idx ? `${gold}12` : navy3, color: activeTab === idx ? gold : muted, cursor: "pointer", transition: "all 0.15s" }}>
            {inst.label}
          </button>
        ))}
      </div>
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24 }}>
        <div style={{ fontSize: "1.1rem", fontWeight: 800, color: white, marginBottom: 8 }}>{current.title}</div>
        <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.7, marginBottom: 14 }}>{current.body}</div>
        <ul style={{ listStyle: "none", padding: 0, marginBottom: 12 }}>
          {current.items.map((it, i) => (
            <li key={i} style={{ display: "flex", gap: 8, fontSize: "0.83rem", color: textCol, padding: "6px 0", borderBottom: i < current.items.length - 1 ? `1px solid ${border}` : "none" }}>
              <span style={{ color: gold, flexShrink: 0, fontSize: "0.75rem", marginTop: 2 }}>•</span>{it}
            </li>
          ))}
        </ul>
        <div>{current.pills.map((p, idx) => <Pill key={idx} text={p.text} color={p.col} />)}</div>
      </div>
    </div>
  );
}

// Section 3: Markets
function MarketsGrid() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "24px 0" }}>
      <ColCard label="Primary Market" labelColor={blue2} accent={blue} title="New Securities Issued" items={["Where new securities are sold for the first time", "The issuer (company or government) receives the proceeds", "Includes Initial Public Offerings (IPOs)", "Also includes ongoing mutual fund unit sales", "Investment dealers often underwrite these issues"]} />
      <ColCard label="Secondary Market" labelColor={gold} accent={gold} title="Existing Securities Trade" items={["Investors trade previously issued securities among themselves", "The issuer receives no proceeds from these trades", "Critical function: provides liquidity to investors", "Enables price discovery and portfolio rebalancing", "Without a secondary market, primary markets would be illiquid"]} />
    </div>
  );
}

function TradingMechanisms() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "24px 0" }}>
      <ColCard label="Auction Market" labelColor={blue2} accent={blue} title="Stock Exchanges" items={["Buyers submit bids (highest price willing to pay)", "Sellers submit asks/offers (lowest price to accept)", "Orders channel to a central market to compete", "High liquidity: frequent sales, narrow spreads, small price fluctuations", "Examples: TSX, TSX Venture, Montreal Exchange, Cboe Canada"]} />
      <ColCard label="Dealer (OTC) Market" labelColor={gold} accent={gold} title="Over-the-Counter" items={["A negotiated market — dealers trade directly with each other", "Via computer terminals and telephone networks", "Dealers post their own bid/ask quotations", "Almost all bonds and debentures trade here", "Also called the 'unlisted' market"]} />
    </div>
  );
}

// Section 4: Intermediaries
function IntermediariesGrid() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "24px 0" }}>
      <ColCard label="Deposit-Taking" labelColor={blue2} accent={blue} title="Banks & Trust Companies" body="Pool deposits from retail customers and lend them out as mortgages and loans. The spread between deposit rates and lending rates is their primary revenue source." items={["Schedule I Banks (domestic)", "Schedule II Banks (foreign subsidiaries)", "Trust companies", "Credit unions"]} />
      <ColCard label="Non-Deposit-Taking" labelColor={gold} accent={gold} title="Investment Dealers & Insurers" body="Life insurers invest premiums received to fund future claims. Investment dealers connect issuers to investors in both primary and secondary markets." items={["Life insurance companies", "Investment dealers (brokerage firms)", "Pension fund managers", "Mutual fund companies"]} />
    </div>
  );
}

// Section 5: Regulatory Framework
function RegulatoryFlow() {
  const steps = [
    { label: "Provincial", title: "Provincial Securities Commissions", body: "Primary regulatory authority. Each province/territory has its own securities act and commission (e.g. OSC in Ontario, AMF in Quebec)." },
    { label: "Umbrella Body", title: "Canadian Securities Administrators (CSA)", body: "An umbrella organization of all provincial/territorial regulators that coordinates and harmonizes securities laws across Canada." },
    { label: "SRO", title: "CIRO — Canadian Investment Regulatory Organization", body: "Formed in 2023 from the merger of IIROC and MFDA. Regulates investment dealers and mutual fund dealers — the distribution side. Does not regulate the funds themselves." },
    { label: "Quebec", title: "Quebec: AMF & Chambre de la sécurité financière", body: "The Autorité des marchés financiers regulates Quebec's financial sector. The Chambre de la sécurité financière oversees ethics and continuing education for Quebec reps." },
  ];
  return (
    <div style={{ margin: "24px 0", position: "relative" }}>
      {steps.map((step, idx) => (
        <div key={idx} style={{ marginBottom: 12, paddingLeft: idx > 0 ? 24 : 0 }}>
          <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 20, position: "relative" }}>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: blue2, marginBottom: 6 }}>{step.label}</div>
            <div style={{ fontSize: "1.05rem", fontWeight: 800, color: white, marginBottom: 6 }}>{step.title}</div>
            <div style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7 }}>{step.body}</div>
          </div>
          {idx < steps.length - 1 && <div style={{ marginLeft: 20, marginTop: 4, marginBottom: 4, fontSize: "1rem", color: blue2 }}>↓</div>}
        </div>
      ))}
    </div>
  );
}

// Interactive Tools: Bond Calculator
function BondCalculator() {
  const [faceValue, setFaceValue] = useState(10000);
  const [bondPrice, setBondPrice] = useState(103.5);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const cost = (bondPrice / 100) * faceValue;
    setResult(cost.toFixed(2));
  };

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "24px 0" }}>
      <div style={{ fontSize: "1rem", fontWeight: 800, color: white, marginBottom: 8 }}>🧮 Bond Purchase Price Calculator</div>
      <div style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.65, marginBottom: 16 }}>
        Bonds are quoted as a <strong style={{ color: gold }}>percentage of their face (par) value</strong>, where 100 = par. A price of 103.5 means the bond trades at a 3.5% premium over face value. Formula: Cost = (Price ÷ 100) × Face Value
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: blue2, marginBottom: 6 }}>Face Value ($)</label>
          <input type="number" value={faceValue} onChange={(e) => setFaceValue(Number(e.target.value))}
            style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: `1px solid ${border}`, background: navy4, color: white, fontFamily: "monospace", fontSize: "0.9rem" }} />
        </div>
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: blue2, marginBottom: 6 }}>Bond Price (e.g. 103.5)</label>
          <input type="number" value={bondPrice} onChange={(e) => setBondPrice(Number(e.target.value))} step="0.1"
            style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: `1px solid ${border}`, background: navy4, color: white, fontFamily: "monospace", fontSize: "0.9rem" }} />
        </div>
      </div>
      <button onClick={calculate}
        style={{ padding: "10px 20px", borderRadius: 6, border: "none", background: gold, color: "#000", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", transition: "all 0.2s" }}>
        Calculate Cost →
      </button>
      {result && (
        <div style={{ marginTop: 16, padding: "14px 18px", background: `${gold}12`, border: `1px solid ${gold}33`, borderRadius: 6 }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: gold, marginBottom: 4 }}>Total Purchase Cost</div>
          <div style={{ fontFamily: "monospace", fontSize: "1.5rem", fontWeight: 800, color: gold }}>${result}</div>
        </div>
      )}
    </div>
  );
}

// Bid-Ask Simulator
function BidAskSimulator() {
  const [bid, setBid] = useState(20.10);
  const [ask, setAsk] = useState(20.15);
  const spread = (ask - bid).toFixed(2);

  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "24px 0" }}>
      <div style={{ fontSize: "1rem", fontWeight: 800, color: white, marginBottom: 16 }}>📊 Bid-Ask Spread Simulator</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: blue2, marginBottom: 6 }}>Bid Price</label>
          <input type="number" value={bid} onChange={(e) => setBid(Number(e.target.value))} step="0.01"
            style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: `1px solid ${border}`, background: navy4, color: white, fontFamily: "monospace", fontSize: "0.9rem" }} />
          <div style={{ fontSize: "0.72rem", color: muted, marginTop: 4 }}>Buyer pays this to sell</div>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 700, color: gold, marginBottom: 6 }}>Ask Price</label>
          <input type="number" value={ask} onChange={(e) => setAsk(Number(e.target.value))} step="0.01"
            style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: `1px solid ${border}`, background: navy4, color: white, fontFamily: "monospace", fontSize: "0.9rem" }} />
          <div style={{ fontSize: "0.72rem", color: muted, marginTop: 4 }}>Buyer pays this to buy</div>
        </div>
      </div>
      <div style={{ padding: "14px 18px", background: `${green}12`, border: `1px solid ${green}33`, borderRadius: 6, marginBottom: 12 }}>
        <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: green, marginBottom: 4 }}>Spread</div>
        <div style={{ fontFamily: "monospace", fontSize: "1.3rem", fontWeight: 800, color: green }}>${spread}</div>
        <div style={{ fontSize: "0.75rem", color: muted, marginTop: 4 }}>Ask minus Bid</div>
      </div>
      <div style={{ position: "relative", height: 40, background: navy4, borderRadius: 6, overflow: "hidden" }}>
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: "50%", background: `${blue}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, color: blue2 }}>
          Bid ${bid.toFixed(2)}
        </div>
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "50%", background: `${gold}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, color: gold }}>
          Ask ${ask.toFixed(2)}
        </div>
      </div>
      <div style={{ fontSize: "0.82rem", color: muted, lineHeight: 1.65, marginTop: 12 }}>
        <strong style={{ color: white }}>Key Rules:</strong> To <strong style={{ color: gold }}>buy immediately</strong>, you pay the Ask price (the seller's minimum). To <strong style={{ color: blue2 }}>sell immediately</strong>, you accept the Bid price (the buyer's maximum). The spread represents the dealer's compensation and is a measure of market liquidity — tighter spreads indicate more liquid markets.
      </div>
    </div>
  );
}

// Quiz Component
const quizData = [
  { q: "Which of the following is a core characteristic of investment capital?", opts: ["It is unlimited in supply", "It is insensitive to the political environment", "It is mobile — it moves to where opportunities and hospitable conditions exist", "It is static and remains in its country of origin"], correct: 2, feedback: { correct: "Correct! Capital is mobile — it moves to locations where the investment climate is hospitable and profit opportunities exist.", incorrect: "Not quite. Capital is mobile — it moves freely across borders and sectors seeking the best risk-adjusted returns." } },
  { q: "Which level of government issues 'Instalment Debentures' to spread the cost of capital projects like sewers and streets over several years?", opts: ["Federal Government", "Provincial Government", "Municipal Government", "Foreign Government"], correct: 2, feedback: { correct: "Correct! Municipal governments issue instalment debentures (serial debentures) to spread infrastructure costs over many years.", incorrect: "Not quite. Municipal governments use instalment debentures for long-term capital projects like roads and sewers." } },
  { q: "When an investor buys shares directly from the issuing company during an IPO, this transaction takes place in the:", opts: ["Secondary Market", "Dealer Market", "Primary Market", "Auction Market"], correct: 2, feedback: { correct: "Correct! The primary market is where new securities are first sold — the issuer receives the proceeds.", incorrect: "Not quite. An IPO occurs in the primary market, where new securities are sold for the first time." } },
  { q: "Which organization was formed by the 2023 amalgamation of IIROC and the MFDA?", opts: ["The CSA", "CIRO", "The OSC", "The AMF"], correct: 1, feedback: { correct: "Correct! CIRO (Canadian Investment Regulatory Organization) was formed from the merger of IIROC and MFDA in 2023.", incorrect: "Not quite. CIRO was created by merging IIROC and the MFDA in 2023." } },
  { q: "Most bonds and debentures in Canada trade in which type of market?", opts: ["Auction Market (Stock Exchange)", "Dealer (OTC) Market", "Futures Market", "Primary Market"], correct: 1, feedback: { correct: "Correct! Most bonds and debentures trade over-the-counter in the dealer market, not on exchanges.", incorrect: "Not quite. Bonds typically trade in the dealer (OTC) market where dealers negotiate directly." } },
  { q: "In an auction market, the 'Bid Price' represents:", opts: ["The lowest price a seller is willing to accept", "The highest price a buyer is willing to pay", "The price at which the last trade occurred", "The commission charged by the dealer"], correct: 1, feedback: { correct: "Correct! The bid is the highest price a buyer will pay. Sellers who want immediate execution accept the bid.", incorrect: "Not quite. The bid is the highest price a buyer is willing to pay for a security." } },
  { q: "Which of the following is considered a 'Financial Intermediary'?", opts: ["The Toronto Stock Exchange (TSX)", "A manufacturing company", "A Schedule I Bank", "The Canadian Securities Administrators (CSA)"], correct: 2, feedback: { correct: "Correct! Schedule I banks are deposit-taking financial intermediaries that facilitate capital flows.", incorrect: "Not quite. Banks, insurers, and investment dealers are financial intermediaries — not exchanges or regulators." } },
  { q: "What is the primary role of the Canadian Securities Administrators (CSA)?", opts: ["To insure client accounts against dealer insolvency", "To coordinate and harmonize securities regulation across Canadian provinces and territories", "To set interest rates for financial institutions", "To manage and operate the Toronto Stock Exchange"], correct: 1, feedback: { correct: "Correct! The CSA is an umbrella body that coordinates and harmonizes securities laws across all provinces and territories.", incorrect: "Not quite. The CSA coordinates regulation across provinces — it doesn't operate exchanges or insure accounts." } },
  { q: "A bond is quoted at a price of 98. This bond is trading at:", opts: ["A premium — above par value", "Par — exactly at face value", "A discount — below par value (less than 100)", "A commission rate"], correct: 2, feedback: { correct: "Correct! A bond price below 100 means it's trading at a discount to par (face value).", incorrect: "Not quite. Bond prices below 100 represent a discount; above 100 is a premium." } },
  { q: "An investor wants to buy a bond with a face value of $25,000. The bond is priced at 103.5. What is the cost of the bond (excluding commissions)?", opts: ["$25,000", "$24,125", "$25,875", "$26,350"], correct: 2, feedback: { correct: "Correct! Cost = (103.5 ÷ 100) × $25,000 = $25,875.", incorrect: "Not quite. Use the formula: (Price ÷ 100) × Face Value = (103.5 ÷ 100) × 25,000 = $25,875." } },
];

function Quiz() {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showScore, setShowScore] = useState(false);

  const handleAnswer = (optIdx) => {
    const isCorrect = optIdx === quizData[currentQ].correct;
    setAnswers(prev => ({ ...prev, [currentQ]: { selected: optIdx, correct: isCorrect } }));
  };

  const nextQuestion = () => {
    if (currentQ < quizData.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      setShowScore(true);
    }
  };

  const retryQuiz = () => {
    setCurrentQ(0);
    setAnswers({});
    setShowScore(false);
  };

  const score = Object.values(answers).filter(a => a.correct).length;
  const currentAnswer = answers[currentQ];

  if (showScore) {
    return (
      <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 40, textAlign: "center", margin: "24px 0" }}>
        <div style={{ fontSize: "3rem", marginBottom: 12 }}>🎉</div>
        <div style={{ fontSize: "1.5rem", fontWeight: 800, color: white, marginBottom: 8 }}>Quiz Complete!</div>
        <div style={{ fontSize: "2.5rem", fontWeight: 800, color: gold, marginBottom: 16 }}>{score}/{quizData.length}</div>
        <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
          {score === quizData.length ? "Perfect score! You've mastered Chapter 2." : score >= 7 ? "Great work! You have a solid grasp of the material." : "Good effort. Review the chapter and try again."}
        </div>
        <button onClick={retryQuiz}
          style={{ padding: "10px 24px", borderRadius: 6, border: "none", background: blue, color: white, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", transition: "all 0.2s" }}>
          ↺ Try Again
        </button>
      </div>
    );
  }

  const question = quizData[currentQ];
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, margin: "24px 0" }}>
      <div style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: blue2, marginBottom: 12 }}>
        Question {currentQ + 1} of {quizData.length}
      </div>
      <div style={{ fontSize: "1rem", fontWeight: 700, color: white, marginBottom: 16 }}>{question.q}</div>
      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        {question.opts.map((opt, idx) => (
          <button key={idx} onClick={() => !currentAnswer && handleAnswer(idx)}
            style={{
              textAlign: "left", padding: "12px 16px", borderRadius: 6,
              border: `1px solid ${currentAnswer && currentAnswer.selected === idx ? (currentAnswer.correct ? green : red) : border}`,
              background: currentAnswer && currentAnswer.selected === idx ? (currentAnswer.correct ? `${green}12` : `${red}12`) : navy4,
              color: currentAnswer && currentAnswer.selected === idx ? (currentAnswer.correct ? green : red) : textCol,
              fontWeight: currentAnswer && currentAnswer.selected === idx ? 600 : 400,
              fontSize: "0.85rem", cursor: currentAnswer ? "default" : "pointer", transition: "all 0.2s"
            }}>
            {opt}
          </button>
        ))}
      </div>
      {currentAnswer && (
        <div style={{ padding: "12px 16px", borderRadius: 6, background: currentAnswer.correct ? `${green}12` : `${red}12`, border: `1px solid ${currentAnswer.correct ? green : red}33`, marginBottom: 16 }}>
          <div style={{ fontSize: "0.85rem", color: currentAnswer.correct ? green : red, lineHeight: 1.7 }}>
            {currentAnswer.correct ? question.feedback.correct : question.feedback.incorrect}
          </div>
        </div>
      )}
      <button onClick={nextQuestion} disabled={!currentAnswer}
        style={{
          padding: "10px 20px", borderRadius: 6, border: "none",
          background: currentAnswer ? gold : muted, color: currentAnswer ? "#000" : "#333",
          fontWeight: 700, fontSize: "0.85rem", cursor: currentAnswer ? "pointer" : "not-allowed",
          opacity: currentAnswer ? 1 : 0.5, transition: "all 0.2s"
        }}>
        {currentQ < quizData.length - 1 ? "Next Question →" : "See My Results →"}
      </button>
    </div>
  );
}

// Glossary
const glossaryTerms = [
  { term: "Capital", def: "Wealth in both tangible form (land, buildings) and representational form (money, stocks, bonds) used to enhance economic growth." },
  { term: "Direct Investment", def: "Investing directly in a physical asset — a home purchase, highway construction, or factory build." },
  { term: "Indirect Investment", def: "Buying a security (stock, bond, fund) where the issuer uses the proceeds for productive investment." },
  { term: "Retail Investor", def: "An individual buying and selling securities for their own personal account (not on behalf of an institution)." },
  { term: "Institutional Investor", def: "Organizations like pension funds, mutual funds, and insurance companies that trade large volumes with steady cash flows." },
  { term: "Debt Instrument", def: "A security where the issuer promises to repay principal at maturity and make regular interest payments. Buyer is a creditor." },
  { term: "Common Share", def: "An equity security granting ownership with voting rights; eligible for dividends and capital gains but last in line in bankruptcy." },
  { term: "Preferred Share", def: "Equity with a prior claim on dividends over common shares; typically non-voting with a fixed dividend rate." },
  { term: "Primary Market", def: "Where new securities are first issued and sold. The issuer receives the proceeds. Includes IPOs and new bond issuances." },
  { term: "Secondary Market", def: "Where previously issued securities are traded among investors. The issuer receives no proceeds. Provides liquidity." },
  { term: "Auction Market", def: "A centralized exchange where buyers bid and sellers offer, with orders competing openly. Examples: TSX, Montreal Exchange." },
  { term: "Dealer (OTC) Market", def: "A negotiated market where dealers trade directly with each other via computer/phone. Most bonds trade here." },
  { term: "Bid Price", def: "The highest price a buyer is willing to pay for a security. A seller who wants to trade immediately accepts the bid." },
  { term: "Ask Price", def: "The lowest price a seller will accept for a security. A buyer who wants to trade immediately pays the ask. Always higher than the bid." },
  { term: "Spread", def: "The difference between the ask and bid price. Narrow spreads indicate high liquidity; wide spreads indicate low liquidity." },
  { term: "Underwriting", def: "When an investment dealer buys an entire new issue from the issuer to resell to investors, accepting the risk of the distribution." },
  { term: "CSA", def: "Canadian Securities Administrators — an umbrella body coordinating and harmonizing securities regulation across all provinces and territories." },
  { term: "T-Bill", def: "Treasury Bill — a short-term debt instrument issued by the federal government, sold at a discount and maturing at face value." },
];

function Glossary() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10, margin: "24px 0" }}>
      {glossaryTerms.map((item, idx) => (
        <div key={idx} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: "16px 18px" }}>
          <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>{item.term}</div>
          <div style={{ fontSize: "0.8rem", color: muted, lineHeight: 1.6 }}>{item.def}</div>
        </div>
      ))}
    </div>
  );
}

// Main Component Export
export default function Chapter2Interactive() {
  return (
    <div style={{ fontFamily: "'Nunito Sans',-apple-system,sans-serif", color: textCol }}>
      {/* Section 1: Investment Capital */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 01</span>
          <span style={s.badge}>Investment Capital</span>
        </div>
        <h2 style={s.h2}>What is Investment Capital?</h2>
        <div style={s.prose}>
          <strong style={{ color: white }}>Capital</strong> is wealth — both real, tangible assets (land, buildings, machinery) and representational instruments (money, stocks, bonds). The goal of capital is to enhance the economic growth prospects of an economy by funding productive investment.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "20px 0" }}>
          <ColCard label="Type 1" labelColor={blue2} accent={blue} title="Direct Investment" body="When an individual or company invests directly in a physical asset." items={["A couple buying a home", "A government building a highway", "A company constructing a new factory"]} />
          <ColCard label="Type 2" labelColor={gold} accent={gold} title="Indirect Investment" body="When an investor buys a security, and the issuer uses the proceeds for productive investment." items={["Buying stocks or bonds", "Purchasing mutual fund units", "Government bond issuance for infrastructure"]} />
        </div>
        <div style={s.prose}>Capital has three critical characteristics that every market participant must understand:</div>
        <CapitalChars />
        <div style={s.prose}>
          <strong style={{ color: white }}>The only source of capital is savings</strong> — when revenue exceeds expenditure, a surplus is created that can be invested. There are three categories of capital suppliers and three categories of users:
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, margin: "20px 0" }}>
          <ColCard label="Suppliers of Capital" labelColor={blue2} accent={blue} title="Who Provides It" items={["Retail Investors — individuals investing their personal savings", "Institutional Investors — pension funds, mutual funds, insurance companies with large, steady cash flows", "Foreign Investors — non-residents investing in Canada; historically critical for manufacturing and resource growth"]} />
          <ColCard label="Users of Capital" labelColor={gold} accent={gold} title="Who Needs It" items={["Individuals — housing (mortgages) and consumer durables (cars, appliances)", "Businesses — operations, equipment renewal, and expansion (internal via retained earnings or external via borrowing/shares)", "Governments — funding deficits and capital projects via securities issuance"]} />
        </div>
        <Callout icon="🏛️" accent={blue}>
          <strong style={{ color: white }}>Government Borrowing Breakdown:</strong> Federal government issues <strong style={{ color: gold }}>T-bills and marketable bonds</strong>. Provincial governments issue <strong style={{ color: gold }}>bonds and savings bonds</strong>. Municipal governments issue <strong style={{ color: gold }}>instalment debentures (serial debentures)</strong> to spread the cost of long-term projects — like sewers and streets — over many years.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 2: Financial Instruments */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 02</span>
          <span style={s.badge}>Securities</span>
        </div>
        <h2 style={s.h2}>Financial Instruments (Securities)</h2>
        <div style={s.prose}>
          Securities are formal legal documents that set out the rights and obligations of buyers and sellers. Each type of instrument serves a distinct purpose in the capital markets ecosystem. Click each tab to explore:
        </div>
        <InstrumentsTabs />
      </section>

      <hr style={s.hr} />

      {/* Section 3: Financial Markets */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 03</span>
          <span style={s.badge}>Market Structure</span>
        </div>
        <h2 style={s.h2}>Financial Markets</h2>
        <div style={s.prose}>
          Financial markets are the infrastructure through which capital is allocated. Understanding the distinctions between market types is essential — these distinctions appear frequently on the exam and in practice.
        </div>
        <MarketsGrid />
        <div style={s.prose}>Secondary markets are further divided into two trading mechanisms:</div>
        <TradingMechanisms />
        <Callout icon="💡" accent={blue}>
          <strong style={{ color: white }}>Key Exam Point:</strong> Stocks typically trade on <strong style={{ color: gold }}>auction markets</strong> (exchanges like the TSX). Bonds and debentures typically trade on the <strong style={{ color: gold }}>dealer (OTC) market</strong>. This distinction is a frequent exam question.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 4: Financial Intermediaries */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 04</span>
          <span style={s.badge}>Intermediaries</span>
        </div>
        <h2 style={s.h2}>Financial Intermediaries</h2>
        <div style={s.prose}>
          Financial intermediaries are organizations that facilitate the movement of capital between suppliers (savers) and users (borrowers). Without them, individual savers and borrowers would struggle to find each other efficiently.
        </div>
        <IntermediariesGrid />
        <Callout icon="🏦" accent={blue}>
          <strong style={{ color: white }}>The Two Roles of Investment Dealers:</strong> In the <strong style={{ color: gold }}>primary market</strong>, dealers act as <em>principals</em> by underwriting new issues — buying the entire issue to resell at a profit — or as agents facilitating the sale. In the <strong style={{ color: gold }}>secondary market</strong>, they act as <em>agents</em> (brokers matching buyers and sellers) or <em>principals</em> (holding inventory to buy/sell for their own account) to maintain liquidity.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 5: Regulatory Framework */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 05</span>
          <span style={s.badge}>Regulation</span>
        </div>
        <h2 style={s.h2}>The Regulatory Framework</h2>
        <div style={s.prose}>
          Canada's securities regulation is primarily a provincial responsibility — unlike many countries with a single federal regulator. This creates a layered system of oversight designed to protect investors while enabling efficient markets.
        </div>
        <RegulatoryFlow />
        <Callout icon="⚠️" accent={red}>
          <strong style={{ color: white }}>Important Distinction:</strong> CIRO regulates the <strong style={{ color: gold }}>dealers and representatives</strong> who distribute and sell securities. The <strong style={{ color: gold }}>mutual funds themselves</strong> are regulated by the provincial securities commissions through prospectus requirements and ongoing disclosure rules — not by CIRO.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Interactive Tools */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Interactive Tools</span>
          <span style={s.badge}>Formulas & Practice</span>
        </div>
        <h2 style={s.h2}>Bond Price & Bid-Ask Calculators</h2>
        <div style={s.prose}>
          Chapter 2 introduces two essential calculations. Use these interactive tools to practice and understand the mechanics before your exam.
        </div>
        <BondCalculator />
        <BidAskSimulator />
      </section>

      <hr style={s.hr} />

      {/* Quiz */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Knowledge Check</span>
          <span style={s.badge}>10 Questions</span>
        </div>
        <h2 style={s.h2}>Test Your Understanding</h2>
        <div style={s.prose}>
          Work through all 10 questions to confirm you've absorbed Chapter 2. Detailed feedback is provided for every answer.
        </div>
        <Quiz />
      </section>

      <hr style={s.hr} />

      {/* Glossary */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Quick Reference</span>
          <span style={s.badge}>Key Terms</span>
        </div>
        <h2 style={s.h2}>Glossary</h2>
        <div style={s.prose}>
          A quick-reference index of all essential terms introduced in Chapter 2.
        </div>
        <Glossary />
      </section>
    </div>
  );
}
